/**
 * ProductFormDrawer — right-side panel for creating/editing a product, on the
 * shared Drawer primitive at a fixed width (a form does not resize, SA-814).
 */

import { useState, useEffect } from 'react'
import { Drawer, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui'
import { apiClient } from '@/services/apiClient'
import type { Product, ProductAttributes } from '@/types/commerce'
import { INVENTORY_UNITS } from '@/constants/inventoryUnits'
import { ALLERGEN_SUGGESTIONS, DIETARY_SUGGESTIONS } from '@/constants/productAttributeSuggestions'
import { uploadProductImage, deleteProductImage } from '@/services/products'
import { ProductImageThumb } from './ProductImageThumb'
import { AttributeChipInput } from '@/components/admin/AttributeChipInput'
import { Switch } from '@/components/ui/Switch'

const EMPTY_ATTRIBUTES: ProductAttributes = { allergens: [], dietary: [], keywords: [] }

interface ProductFormDrawerProps {
  open: boolean
  product: Product | null // null = create mode
  onClose: () => void
  onSaved: () => void // page refetches + closes
  onImageChanged?: () => void
}

export function ProductFormDrawer({ open, product, onClose, onSaved, onImageChanged }: ProductFormDrawerProps) {
  // Form state
  const [formName, setFormName] = useState('')
  const [formCategory, setFormCategory] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [formPrice, setFormPrice] = useState('')
  const [formServes, setFormServes] = useState('')
  const [formAlias, setFormAlias] = useState('')
  const [formLeadDays, setFormLeadDays] = useState('0')
  const [formInventoryTracked, setFormInventoryTracked] = useState(false)
  const [formDailyCapacity, setFormDailyCapacity] = useState('')
  const [formInventoryUnit, setFormInventoryUnit] = useState('items')
  const [formLookahead, setFormLookahead] = useState('30')
  const [formImage, setFormImage] = useState<File | null>(null)
  const [formAttributes, setFormAttributes] = useState<ProductAttributes>(EMPTY_ATTRIBUTES)
  const [formIsAvailable, setFormIsAvailable] = useState(true)
  const [formSaving, setFormSaving] = useState(false)

  const resetForm = () => {
    setFormName('')
    setFormCategory('')
    setFormDescription('')
    setFormPrice('')
    setFormServes('')
    setFormAlias('')
    setFormLeadDays('0')
    setFormInventoryTracked(false)
    setFormDailyCapacity('')
    setFormInventoryUnit('items')
    setFormLookahead('30')
    setFormImage(null)
    setFormAttributes(EMPTY_ATTRIBUTES)
    setFormIsAvailable(true)
  }

  useEffect(() => {
    if (!open) return
    if (product) {
      setFormName(product.name)
      setFormCategory(product.category)
      setFormDescription(product.description || '')
      setFormPrice(product.priceCents !== null ? String(product.priceCents / 100) : '')
      setFormServes(product.serves || '')
      setFormAlias(product.alias || '')
      setFormLeadDays(String(product.leadTimeDays))
      setFormInventoryTracked(product.inventoryTracked)
      setFormDailyCapacity(product.defaultDailyCapacity !== null ? String(product.defaultDailyCapacity) : '')
      setFormInventoryUnit(product.inventoryUnit || 'items')
      setFormLookahead(String(product.availabilityLookaheadDays))
      setFormImage(null)
      setFormAttributes(product.attributes ?? EMPTY_ATTRIBUTES)
      setFormIsAvailable(product.isAvailable)
    } else {
      resetForm()
    }
  }, [open, product])

  const handleSave = async () => {
    if (!formName || !formCategory) return
    setFormSaving(true)
    try {
      const body: Record<string, unknown> = {
        name: formName,
        category: formCategory,
        description: formDescription || null,
        price_cents: formPrice ? Math.round(parseFloat(formPrice) * 100) : null,
        serves: formServes || null,
        alias: formAlias || null,
        lead_time_days: parseInt(formLeadDays) || 0,
        inventory_tracked: formInventoryTracked,
        default_daily_capacity: formDailyCapacity === '' ? null : Number(formDailyCapacity),
        inventory_unit: formInventoryUnit,
        availability_lookahead_days: Number(formLookahead || 30),
        attributes: formAttributes,
        is_available: formIsAvailable,
      }

      let productId = product?.id
      if (product) {
        // Migrate-on-save: attributes are now authoritative for this product —
        // null the legacy flat tags so cleared chips can't resurrect from the
        // effective_attributes fold on reload. Create path + API writers keep
        // the fold; this fires only from the form's edit save.
        await apiClient.fetch(`/api/commerce/products/${product.id}`, {
          method: 'PUT', body: JSON.stringify({ ...body, tags: null }),
        })
      } else {
        const res = await apiClient.fetch('/api/commerce/products', {
          method: 'POST', body: JSON.stringify(body),
        })
        const created = await res.json()
        productId = created.id
      }
      if (formImage && productId) {
        await uploadProductImage(productId, formImage)
      }
      onSaved()
    } finally {
      setFormSaving(false)
    }
  }

  if (!open) return null

  return (
    <Drawer open onOpenChange={(o) => { if (!o) onClose() }}>
      <DrawerContent className="max-w-lg">
        <DrawerHeader>
          <DrawerTitle>{product ? `Edit: ${product.name}` : 'New Product'}</DrawerTitle>
        </DrawerHeader>

        <DrawerBody className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="product-name" className="block text-xs font-medium text-muted-foreground mb-1">Name *</label>
              <input id="product-name" value={formName} onChange={e => setFormName(e.target.value)}
                className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-card" />
            </div>
            <div>
              <label htmlFor="product-category" className="block text-xs font-medium text-muted-foreground mb-1">Category *</label>
              <input id="product-category" value={formCategory} onChange={e => setFormCategory(e.target.value)} placeholder="e.g. pasteles"
                className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-card" />
            </div>
          </div>
          <div>
            <label htmlFor="product-description" className="block text-xs font-medium text-muted-foreground mb-1">Description</label>
            <textarea id="product-description" value={formDescription} onChange={e => setFormDescription(e.target.value)} rows={2}
              className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-card" />
          </div>
          <div>
            <label htmlFor="product-image" className="block text-xs font-medium text-muted-foreground mb-1">Product Image</label>
            <input
              id="product-image"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={e => setFormImage(e.target.files?.[0] ?? null)}
              className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-card"
            />
            {product?.hasImage && (
              <div className="mt-2 flex items-center gap-2">
                <ProductImageThumb productId={product.id} hasImage={true} />
                <button
                  type="button"
                  onClick={async () => { await deleteProductImage(product.id); setFormImage(null); onImageChanged?.() }}
                  className="text-xs text-destructive"
                >Remove image</button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label htmlFor="product-price" className="block text-xs font-medium text-muted-foreground mb-1">Price (empty = quote)</label>
              <input id="product-price" type="number" value={formPrice} onChange={e => setFormPrice(e.target.value)} placeholder="825"
                className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-card" />
            </div>
            <div>
              <label htmlFor="product-serves" className="block text-xs font-medium text-muted-foreground mb-1">Serves</label>
              <input id="product-serves" value={formServes} onChange={e => setFormServes(e.target.value)} placeholder="10 personas"
                className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-card" />
            </div>
            <div>
              <label htmlFor="product-lead-days" className="block text-xs font-medium text-muted-foreground mb-1">Lead Time (days)</label>
              <input id="product-lead-days" type="number" value={formLeadDays} onChange={e => setFormLeadDays(e.target.value)}
                className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-card" />
            </div>
          </div>
          <div>
            <label htmlFor="product-alias" className="block text-xs font-medium text-muted-foreground mb-1">Conversational name</label>
            <input id="product-alias" value={formAlias} onChange={e => setFormAlias(e.target.value)}
              placeholder='e.g. "Oreo cupcake" — used in chat prose'
              className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-card" />
          </div>
          {/* Availability + attributes (UI/UX pass PR 2) */}
          <div className="flex items-center justify-between rounded-sm border border-border px-3 py-2">
            <div>
              <p className="text-xs font-medium text-foreground">Available</p>
              <p className="text-[11px] text-muted-foreground max-w-xs">
                Disabled products are hidden from menus and ordering. If a customer
                asks for one, the persona says it's temporarily unavailable and the
                team is notified in the Inbox.
              </p>
            </div>
            <Switch checked={formIsAvailable} onCheckedChange={setFormIsAvailable} aria-label="Available" />
          </div>
          <AttributeChipInput label="Allergens / contains" inputId="product-allergens"
            values={formAttributes.allergens} suggestions={ALLERGEN_SUGGESTIONS}
            onChange={allergens => setFormAttributes(a => ({ ...a, allergens }))} />
          <AttributeChipInput label="Dietary" inputId="product-dietary"
            values={formAttributes.dietary} suggestions={DIETARY_SUGGESTIONS}
            onChange={dietary => setFormAttributes(a => ({ ...a, dietary }))} />
          <AttributeChipInput label="Keywords" inputId="product-keywords"
            values={formAttributes.keywords}
            onChange={keywords => setFormAttributes(a => ({ ...a, keywords }))} />
          {product && product.tags && product.tags.length > 0 && (
            <p className="text-[11px] text-muted-foreground">
              Legacy tags (replaced on save): {product.tags.join(', ')}
            </p>
          )}
          {/* Inventory fields */}
          <div className="grid grid-cols-3 gap-3">
            <div className="flex items-center gap-2 pt-4">
              <input type="checkbox" id="formInventoryTracked" checked={formInventoryTracked}
                onChange={e => setFormInventoryTracked(e.target.checked)}
                className="rounded-sm border-border" />
              <label htmlFor="formInventoryTracked" className="text-xs font-medium text-muted-foreground">Track inventory</label>
            </div>
            <div>
              <label htmlFor="product-daily-capacity" className="block text-xs font-medium text-muted-foreground mb-1">Daily capacity</label>
              <input id="product-daily-capacity" type="number" value={formDailyCapacity} onChange={e => setFormDailyCapacity(e.target.value)}
                placeholder="e.g. 20" disabled={!formInventoryTracked}
                className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-card disabled:opacity-50" />
            </div>
            <div>
              <label htmlFor="product-inventory-unit" className="block text-xs font-medium text-muted-foreground mb-1">Unit</label>
              <select id="product-inventory-unit" value={formInventoryUnit} onChange={e => setFormInventoryUnit(e.target.value)}
                disabled={!formInventoryTracked}
                className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-card disabled:opacity-50">
                {INVENTORY_UNITS.map(u => (
                  <option key={u.value} value={u.value}>{u.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label htmlFor="product-lookahead" className="block text-xs font-medium text-muted-foreground mb-1">Availability lookahead (days)</label>
              <input id="product-lookahead" type="number" value={formLookahead} onChange={e => setFormLookahead(e.target.value)}
                disabled={!formInventoryTracked}
                className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-card disabled:opacity-50" />
            </div>
          </div>
        </DrawerBody>

        <DrawerFooter>
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-muted-foreground">Cancel</button>
          <button onClick={handleSave} disabled={formSaving || !formName || !formCategory}
            className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 disabled:opacity-50">
            {formSaving ? 'Saving...' : product ? 'Update' : 'Create'}
          </button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
